import { useCallback } from "react";
import type { Task } from "~/types/api";
import { useWindowDataLoader } from "./apis/use-window-data-loader";
import { useTabWindowsActions } from "./use-tab-windows-context";
import type { ExplorerItem } from "../../task.types";
import {
  ALL_BRAND_TASKS_TITLE,
  ALL_TASKS_BRAND_PREFIX,
  ALL_TASKS_PREFIX,
  ALL_TASKS_TITLE,
  groupItemsByStatus,
  restoreMinimizedWindow,
} from "../utils/window-helpers";

export const useOpenTaskWindow = () => {
  const { addWindow, updateWindowState, getWindow } = useTabWindowsActions<ExplorerItem>();
  const { fetchTaskWindowData } = useWindowDataLoader();

  const openTaskWindow = useCallback(
    async (task: Task) => {
      const newWindowId = `window-task-${task.id}`;
      if (restoreMinimizedWindow(getWindow, updateWindowState, newWindowId)) {
        return;
      }

      try {
        const { items, statuses } = await fetchTaskWindowData([task]);

        if (statuses.length > 0) {
          const groupedItems = groupItemsByStatus(items, statuses);
          addWindow(groupedItems, newWindowId, task.name, { taskIds: [task.id] });
        } else {
          addWindow(items, newWindowId, task.name, { taskIds: [task.id] });
        }
      } catch (e) {
        console.error("Failed to fetch items or statuses", e);
      }
    },
    [addWindow, updateWindowState, getWindow, fetchTaskWindowData]
  );

  const openAllTasksWindow = useCallback(
    async (tasks: Task[], brandId?: number, brandName?: string) => {
      const windowId = brandId ? `${ALL_TASKS_BRAND_PREFIX}${brandId}` : ALL_TASKS_PREFIX;
      if (restoreMinimizedWindow(getWindow, updateWindowState, windowId)) {
        return;
      }

      try {
        const { items, statuses } = await fetchTaskWindowData(tasks);
        const taskIds = tasks.map((t) => t.id);

        const title = brandName ? `${brandName}${ALL_BRAND_TASKS_TITLE}` : ALL_TASKS_TITLE;
        if (statuses.length > 0) {
          const groupedItems = groupItemsByStatus(items, statuses);
          addWindow(groupedItems, windowId, title, { taskIds });
        } else {
          addWindow(items, windowId, title, { taskIds }); //TODO: Check this
        }
      } catch (e) {
        console.error("Failed to open All Tasks window", { brandId }, e);
      }
    },
    [addWindow, updateWindowState, getWindow, fetchTaskWindowData]
  );

  const refreshTasksWindow = useCallback(
    async (windowId: string, tasks: Task[], title: string, subtitle?: string) => {
      try {
        const { items, statuses } = await fetchTaskWindowData(tasks);
        const taskIds = tasks.map((t) => t.id);
        const groupedItems = statuses.length > 0 ? groupItemsByStatus(items, statuses) : items;
        addWindow(groupedItems, windowId, title, { taskIds, subtitle });
      } catch (e) {
        console.error("Failed to refresh tasks window", windowId, e);
      }
    },
    [addWindow, fetchTaskWindowData]
  );

  return { openTaskWindow, openAllTasksWindow, refreshTasksWindow };
};
